import React, { useState, useEffect } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { orderAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import './OrderDetail.css';

const STATUS_COLORS = { PENDING:'warning', CONFIRMED:'info', IN_PROGRESS:'info', COMPLETED:'success', CANCELLED:'error', REFUNDED:'error' };
const STATUS_STEPS = ['PENDING', 'CONFIRMED', 'IN_PROGRESS', 'COMPLETED'];

export default function OrderDetail() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (searchParams.get('success')) toast.success('🎉 Payment successful! Your order is confirmed.');
    orderAPI.getById(id).then(r => setOrder(r.data)).catch(console.error).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="loading-page"><div className="spinner" /></div>;
  if (!order) return <div className="page container"><h2>Order not found</h2></div>;

  const isBuyer = user.id === order.buyerId;
  const isSeller = user.id === order.sellerId;
  const stepIndex = STATUS_STEPS.indexOf(order.status);
  const fallback = `https://ui-avatars.com/api/?name=${encodeURIComponent(order.service?.title || 'Order')}&size=200&background=FDE68A&color=92400E&format=svg`;

  return (
    <div className="order-detail-page page">
      <div className="container">
        <div className="flex items-center gap-2 mb-2">
          <Link to={isBuyer ? '/dashboard/buyer' : '/dashboard/seller'} className="text-light text-sm">← Back to Dashboard</Link>
        </div>

        <div className="order-detail-header">
          <div>
            <h1>Order #{order.id.slice(-8).toUpperCase()}</h1>
            <span className={`badge badge-${STATUS_COLORS[order.status]}`} style={{ fontSize: '0.9rem', padding: '6px 14px' }}>{order.status}</span>
          </div>
          <div className="text-light text-sm">{new Date(order.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>
        </div>

        {/* Progress Tracker */}
        {!['CANCELLED', 'REFUNDED'].includes(order.status) && (
          <div className="progress-tracker">
            {STATUS_STEPS.map((step, i) => (
              <div key={step} className={`progress-step ${i <= stepIndex ? 'done' : ''} ${i === stepIndex ? 'current' : ''}`}>
                <div className="progress-dot">{i < stepIndex ? '✓' : i + 1}</div>
                <div className="progress-label">{step.replace('_', ' ')}</div>
                {i < STATUS_STEPS.length - 1 && <div className={`progress-line ${i < stepIndex ? 'done' : ''}`} />}
              </div>
            ))}
          </div>
        )}

        <div className="order-detail-grid">
          {/* Left */}
          <div>
            {/* Service Info */}
            <div className="order-section">
              <h3>Service</h3>
              <div className="service-summary">
                <img
                  src={order.service?.images?.[0] ? `http://localhost:5000${order.service.images[0]}` : fallback}
                  alt={order.service?.title}
                  onError={e => { e.target.src = fallback; }}
                />
                <div>
                  <h4><Link to={`/services/${order.serviceId}`} className="text-primary">{order.service?.title}</Link></h4>
                  <p className="text-light text-sm">{order.service?.category?.name}</p>
                </div>
              </div>
              {order.notes && (
                <div className="order-notes">
                  <strong>📝 Order Notes:</strong>
                  <p>{order.notes}</p>
                </div>
              )}
            </div>

            {/* Contact */}
            {isBuyer && (
              <div className="order-section">
                <h3>Seller</h3>
                <Link to={`/sellers/${order.sellerId}`} className="contact-card">
                  <div className="avatar avatar-lg">
                    {order.seller?.avatar
                      ? <img src={`http://localhost:5000${order.seller.avatar}`} alt={order.seller.firstName} />
                      : `${order.seller?.firstName?.[0]}${order.seller?.lastName?.[0]}`
                    }
                  </div>
                  <div>
                    <div className="font-semibold">{order.seller?.businessName || `${order.seller?.firstName} ${order.seller?.lastName}`}</div>
                    <div className="text-light text-sm">{order.seller?.email}</div>
                  </div>
                </Link>
                <Link to="/messages" className="btn btn-outline btn-sm mt-1">💬 Message Seller</Link>
              </div>
            )}
            {isSeller && (
              <div className="order-section">
                <h3>Customer</h3>
                <div className="contact-card">
                  <div className="avatar avatar-lg">
                    {order.buyer?.avatar
                      ? <img src={`http://localhost:5000${order.buyer.avatar}`} alt={order.buyer.firstName} />
                      : `${order.buyer?.firstName?.[0]}${order.buyer?.lastName?.[0]}`
                    }
                  </div>
                  <div>
                    <div className="font-semibold">{order.buyer?.firstName} {order.buyer?.lastName}</div>
                    <div className="text-light text-sm">{order.buyer?.email}</div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right: Payment Summary */}
          <div>
            <div className="payment-summary">
              <h3>Payment Summary</h3>
              <div className="payment-row">
                <span>Service Price</span>
                <span>${order.totalAmount?.toFixed(2)}</span>
              </div>
              <div className="payment-row">
                <span>Platform Fee</span>
                <span>-${order.platformFee?.toFixed(2)}</span>
              </div>
              {isSeller && (
                <div className="payment-row payment-total">
                  <span>Your Earnings</span>
                  <span className="text-success">${order.sellerAmount?.toFixed(2)}</span>
                </div>
              )}
              {isBuyer && (
                <div className="payment-row payment-total">
                  <span>Total Paid</span>
                  <span>${order.totalAmount?.toFixed(2)}</span>
                </div>
              )}
              {order.paidAt && (
                <div className="payment-row text-sm text-light">
                  <span>Paid</span>
                  <span>{new Date(order.paidAt).toLocaleDateString()}</span>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="order-actions">
              {isBuyer && order.status === 'PENDING' && (
                <button className="btn btn-danger btn-block" onClick={() => orderAPI.updateStatus(id, 'CANCELLED').then(() => window.location.reload())}>
                  Cancel Order
                </button>
              )}
              {isSeller && order.status === 'CONFIRMED' && (
                <button className="btn btn-secondary btn-block" onClick={() => orderAPI.updateStatus(id, 'IN_PROGRESS').then(() => window.location.reload())}>
                  Start Work
                </button>
              )}
              {isSeller && order.status === 'IN_PROGRESS' && (
                <button className="btn btn-primary btn-block" onClick={() => orderAPI.updateStatus(id, 'COMPLETED').then(() => window.location.reload())}>
                  Mark as Completed
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
